import { __assign, __awaiter, __generator } from "tslib";
import { isDefined, isNumber, isString } from '@tyler-components-web/core';
import { DIALOG_CONSTANTS } from './dialog-constants';
var DialogFoundation = /** @class */ (function () {
    function DialogFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        // Private vars
        this._open = false;
        this._backdropClose = true;
        this._escapeClose = true;
        this._fullscreen = false;
        this._positionType = 'absolute';
        this._positionX = null;
        this._positionY = null;
        this._moveable = false;
        this._moveTarget = DIALOG_CONSTANTS.selectors.DFEAULT_MOVE_TARGET;
        this._isAnimating = false;
        this._isMoving = false;
        // Event handler states
        this._attachedDocumentKeydownHandler = false;
        this._attachedBackdropClickHandler = false;
        this._transitionEndHandler = function (evt) { return _this._onTransitionEnd(); };
        this._documentKeydownHandler = function (evt) { return _this._onDocumentKeydown(evt); };
        this._backdropClickHandler = function (evt) { return _this._onBackdropClick(evt); };
        this._moveTargetMouseDownHandler = function (evt) { return _this._onMoveTargetMouseDown(evt); };
        this._moveTargetMouseMoveHandler = function (evt) { return _this._onMoveTargetMouseMove(evt); };
        this._moveTargetMouseUpHandler = function (evt) { return _this._onMoveTargetMouseUp(evt); };
    }
    DialogFoundation.prototype.initialize = function () {
        this._adapter.initializeAccessibility();
        if (this._open) {
            if (this._moveable) {
                this._adapter.setMoveable(this._moveable);
                this._initMoveTarget();
            }
        }
    };
    DialogFoundation.prototype.destroy = function () {
        if (this._open) {
            this._removeDragHandlers();
            this._adapter.deregisterTransitionEndHandler(this._transitionEndHandler);
            if (this._attachedDocumentKeydownHandler) {
                this._adapter.removeDocumentListener('keydown', this._documentKeydownHandler);
            }
            if (this._attachedBackdropClickHandler) {
                this._adapter.deregisterBackdropClickHandler(this._backdropClickHandler);
            }
        }
    };
    Object.defineProperty(DialogFoundation.prototype, "backdropClose", {
        get: function () {
            return this._backdropClose;
        },
        /** Controls whether clicking the backdrop closes the dialog or not. */
        set: function (value) {
            value = Boolean(value);
            if (this._backdropClose !== value) {
                this._backdropClose = value;
                // TODO: Destroy the current focus trap and renable with the new option value
                this._setBackdropClickListener(this._backdropClose);
                if (isDefined(this._backdropClose)) {
                    this._adapter.setHostAttribute(DIALOG_CONSTANTS.attributes.BACKDROP_CLOSE, this._backdropClose.toString());
                }
                else {
                    this._adapter.removeHostAttribute(DIALOG_CONSTANTS.attributes.BACKDROP_CLOSE);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "escapeClose", {
        get: function () {
            return this._escapeClose;
        },
        /** Controls whether pressing the escape key closes the dialog or not. */
        set: function (value) {
            value = Boolean(value);
            if (this._escapeClose !== value) {
                this._escapeClose = value;
                this._setDocumentKeydownListener(this._escapeClose);
                if (isDefined(this._escapeClose)) {
                    this._adapter.setHostAttribute(DIALOG_CONSTANTS.attributes.ESCAPE_CLOSE, this._escapeClose.toString());
                }
                else {
                    this._adapter.removeHostAttribute(DIALOG_CONSTANTS.attributes.ESCAPE_CLOSE);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "open", {
        get: function () {
            return this._open;
        },
        set: function (value) {
            var _this = this;
            if (this._open !== value) {
                value = Boolean(value);
                if (value !== this._open) {
                    if (value) {
                        if (this._openCallback) {
                            Promise.resolve(this._openCallback())
                                .then(function () {
                                _this._open = value;
                                _this._openDialog();
                            })
                                .catch(function () { });
                        }
                        else {
                            this._open = value;
                            this._openDialog();
                        }
                    }
                    else {
                        if (this._closeCallback) {
                            Promise.resolve(this._closeCallback())
                                .then(function () {
                                _this._open = value;
                                _this._closeDialog();
                            })
                                .catch(function () { });
                        }
                        else {
                            this._open = value;
                            this._closeDialog();
                        }
                    }
                }
                if (isDefined(this._open)) {
                    this._adapter.setHostAttribute(DIALOG_CONSTANTS.attributes.OPEN, this._open.toString());
                }
                else {
                    this._adapter.removeHostAttribute(DIALOG_CONSTANTS.attributes.OPEN);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "fullscreen", {
        get: function () {
            return this._fullscreen;
        },
        set: function (value) {
            if (this._fullscreen !== value) {
                this._fullscreen = value;
                this._adapter.setFullscreen(this._fullscreen);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "openCallback", {
        set: function (callback) {
            this._openCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "closeCallback", {
        set: function (callback) {
            this._closeCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "beforeCloseCallback", {
        set: function (callback) {
            this._beforeCloseCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "positionType", {
        get: function () {
            return this._positionType;
        },
        set: function (value) {
            if (this._positionType !== value) {
                this._positionType = value;
                if (this._open) {
                    this._adapter.setSurfacePosition(this._positionX, this._positionY, this._positionType);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "positionX", {
        get: function () {
            return this._positionX;
        },
        set: function (value) {
            if (this._positionX !== value) {
                this._positionX = this._normalizePositionValue(value);
                if (this._open) {
                    this._adapter.setSurfacePosition(this._positionX, this._positionY, this._positionType);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "positionY", {
        get: function () {
            return this._positionY;
        },
        set: function (value) {
            if (this._positionY !== value) {
                this._positionY = this._normalizePositionValue(value);
                if (this._open) {
                    this._adapter.setSurfacePosition(this._positionX, this._positionY, this._positionType);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "moveable", {
        get: function () {
            return this._moveable;
        },
        set: function (value) {
            if (this._moveable !== value) {
                this._moveable = value;
                if (this._open) {
                    this._adapter.setMoveable(this._moveable);
                    if (this._moveable) {
                        this._initMoveTarget();
                    }
                    else {
                        this._removeDragHandlers();
                        this._isMoving = false;
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogFoundation.prototype, "moveTarget", {
        get: function () {
            return this._moveTarget;
        },
        set: function (value) {
            this._moveTarget = value;
        },
        enumerable: true,
        configurable: true
    });
    DialogFoundation.prototype.initializeMoveTarget = function () {
        if (!this._moveTarget) {
            this._moveTarget = DIALOG_CONSTANTS.selectors.DFEAULT_MOVE_TARGET;
        }
        this._initMoveTarget();
    };
    DialogFoundation.prototype.resetPosition = function () {
        this._adapter.setSurfacePosition(this._positionX, this._positionY, this._positionType);
    };
    DialogFoundation.prototype._normalizePositionValue = function (value) {
        if (isNumber(value)) {
            return value + "px";
        }
        else if (isString(value)) {
            return value;
        }
        return null;
    };
    DialogFoundation.prototype._openDialog = function () {
        var _this = this;
        this._adapter.attach();
        if (!this._fullscreen) {
            if (this._moveable) {
                this._adapter.setMoveable(this._moveable);
            }
            if (this._positionX !== null || this._positionY !== null) {
                this._adapter.setSurfacePosition(this._positionX, this._positionY, this._positionType);
            }
        }
        this._adapter.setBodyAttribute(DIALOG_CONSTANTS.attributes.OPEN, 'true');
        this._adapter.registerTransitionEndHandler(this._transitionEndHandler);
        this._setDocumentKeydownListener(this._escapeClose);
        this._setBackdropClickListener(this._backdropClose);
        this._adapter.setAnimating(true);
        this._isAnimating = true;
        // Ensure transitions are triggered properly
        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                _this._adapter.setVisibility(true);
                _this._adapter.emitHostEvent(DIALOG_CONSTANTS.events.OPEN, true);
                _this._adapter.trySetInitialFocus();
                if (_this._adapter.isScrollable()) {
                    _this._adapter.addRootClass(DIALOG_CONSTANTS.classes.SCROLLABLE);
                }
            });
        });
        // Wait for the dialog to finish animating open, then emit the ready event and attach any listeners
        setTimeout(function () {
            window.requestAnimationFrame(function () {
                if (_this._open && _this._isAnimating) {
                    _this._onTransitionEnd();
                }
            });
        }, DIALOG_CONSTANTS.numbers.ANIMATION_DURATION);
    };
    DialogFoundation.prototype._closeDialog = function () {
        var _this = this;
        if (this._moveTarget) {
            this._removeDragHandlers();
        }
        this._adapter.deregisterTransitionEndHandler(this._transitionEndHandler);
        if (this._attachedDocumentKeydownHandler) {
            this._adapter.removeDocumentListener('keydown', this._documentKeydownHandler);
        }
        if (this._attachedBackdropClickHandler) {
            this._adapter.deregisterBackdropClickHandler(this._backdropClickHandler);
        }
        this._isAnimating = false;
        this._adapter.setAnimating(true);
        this._adapter.setVisibility(false);
        setTimeout(function () {
            _this._adapter.emitHostEvent(DIALOG_CONSTANTS.events.CLOSE, true);
            _this._adapter.detach();
            var openDialogs = _this._adapter.getOpenDialogs(DIALOG_CONSTANTS.elementName);
            if (!openDialogs.length) {
                _this._adapter.removeBodyAttribute(DIALOG_CONSTANTS.attributes.OPEN);
            }
        }, DIALOG_CONSTANTS.numbers.ANIMATION_DURATION);
    };
    DialogFoundation.prototype._onTransitionEnd = function () {
        if (!this._isAnimating) {
            return;
        }
        this._adapter.deregisterTransitionEndHandler(this._transitionEndHandler);
        this._adapter.setAnimating(false);
        this._adapter.emitHostEvent(DIALOG_CONSTANTS.events.READY, true);
        this._adapter.tryLayoutChildren();
        if (this._moveable) {
            this._initMoveTarget();
        }
        this._isAnimating = false;
    };
    DialogFoundation.prototype._initMoveTarget = function () {
        if (!this._fullscreen && this._moveable && this._moveTarget && this._adapter.setMoveTarget(this._moveTarget)) {
            this._adapter.setMoveTargetHandler('mousedown', this._moveTargetMouseDownHandler);
        }
    };
    DialogFoundation.prototype._removeDragHandlers = function () {
        this._adapter.removeDragTargetHandler('mousedown', this._moveTargetMouseDownHandler);
        this._adapter.removeDocumentListener('mousemove', this._moveTargetMouseMoveHandler);
        this._adapter.removeDocumentListener('mouseup', this._moveTargetMouseUpHandler);
    };
    DialogFoundation.prototype._onMoveTargetMouseDown = function (evt) {
        evt.preventDefault();
        this._adapter.captureActiveElement();
        var bounds = this._adapter.getSurfaceBounds();
        this._moveContext = {
            top: evt.clientY - bounds.top,
            left: evt.clientX - bounds.left,
            height: bounds.height,
            width: bounds.width
        };
        this._adapter.setDocumentListener('mousemove', this._moveTargetMouseMoveHandler);
        this._adapter.setDocumentListener('mouseup', this._moveTargetMouseUpHandler);
    };
    DialogFoundation.prototype._onMoveTargetMouseMove = function (evt) {
        evt.preventDefault();
        var position = this._calculateOffsetPosition(evt.pageX, evt.pageY, this._moveContext);
        // If this is the beginning of the move sequence, we emit the start event (to allow for preventing default) and
        // then update the surface position if not prevented
        if (!this._isMoving) {
            this._isMoving = true;
            var canDrag = this._adapter.emitHostEvent(DIALOG_CONSTANTS.events.MOVE_START, position, true, true);
            if (!canDrag) {
                return;
            }
        }
        // Ensure that the surface position stays within the bounds of the screen
        var newPosition = this._clampPosition(position, this._moveContext);
        // Only update the position if it actually changed
        if (!this._lastPosition || newPosition.x !== this._lastPosition.x || newPosition.y !== this._lastPosition.y) {
            var canMove = this._adapter.emitHostEvent(DIALOG_CONSTANTS.events.MOVED, newPosition, true, true);
            if (canMove) {
                this._lastPosition = __assign({}, newPosition);
                var newX = this._normalizePositionValue(newPosition.x);
                var newY = this._normalizePositionValue(newPosition.y);
                this._adapter.setSurfacePosition(newX, newY, 'absolute');
            }
        }
    };
    DialogFoundation.prototype._onMoveTargetMouseUp = function (evt) {
        if (this._isMoving) {
            this._adapter.emitHostEvent(DIALOG_CONSTANTS.events.MOVE_END, true);
        }
        this._moveComplete();
    };
    DialogFoundation.prototype._moveComplete = function () {
        this._adapter.tryRestoreActiveElement();
        this._adapter.removeDocumentListener('mousemove', this._moveTargetMouseMoveHandler);
        this._adapter.removeDocumentListener('mouseup', this._moveTargetMouseUpHandler);
        this._lastPosition = undefined;
        this._moveContext = undefined;
        this._isMoving = false;
    };
    DialogFoundation.prototype._onDocumentKeydown = function (evt) {
        evt.stopPropagation();
        if (evt.key && (evt.key === 'Escape' || evt.key === 'Esc')) {
            this._tryClose();
        }
    };
    DialogFoundation.prototype._onBackdropClick = function (evt) {
        evt.stopPropagation();
        this._tryClose();
    };
    DialogFoundation.prototype._tryClose = function () {
        return __awaiter(this, void 0, void 0, function () {
            var isCancelled, shouldClose, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        isCancelled = !this._adapter.emitHostEvent(DIALOG_CONSTANTS.events.BEFORE_CLOSE, true, undefined, true);
                        if (isCancelled) {
                            return [2 /*return*/];
                        }
                        if (!this._beforeCloseCallback) {
                            this.open = false;
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, Promise.resolve(this._beforeCloseCallback())];
                    case 2:
                        shouldClose = _a.sent();
                        if (shouldClose) {
                            this.open = false;
                            return [2 /*return*/];
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _a.sent();
                        return [2 /*return*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    DialogFoundation.prototype._setBackdropClickListener = function (attach) {
        if (!this._open) {
            return;
        }
        if (attach && !this._attachedBackdropClickHandler) {
            this._adapter.registerBackdropClickHandler(this._backdropClickHandler);
            this._attachedBackdropClickHandler = true;
        }
        else if (!attach && this._attachedBackdropClickHandler) {
            this._adapter.deregisterBackdropClickHandler(this._backdropClickHandler);
            this._attachedBackdropClickHandler = false;
        }
    };
    DialogFoundation.prototype._setDocumentKeydownListener = function (attach) {
        if (!this._open) {
            return;
        }
        if (attach && !this._attachedDocumentKeydownHandler) {
            this._adapter.setDocumentListener('keydown', this._documentKeydownHandler);
            this._attachedDocumentKeydownHandler = true;
        }
        else if (!attach && this._attachedDocumentKeydownHandler) {
            this._adapter.removeDocumentListener('keydown', this._documentKeydownHandler);
            this._attachedDocumentKeydownHandler = false;
        }
    };
    DialogFoundation.prototype._calculateOffsetPosition = function (pageX, pageY, context) {
        return {
            x: pageX - context.left,
            y: pageY - context.top
        };
    };
    DialogFoundation.prototype._clampPosition = function (_a, context) {
        var x = _a.x, y = _a.y;
        if (x <= 0) {
            x = 0;
        }
        else if (x + context.width >= window.innerWidth) {
            x = window.innerWidth - context.width;
        }
        if (y <= 0) {
            y = 0;
        }
        else if (y + context.height >= window.innerHeight) {
            y = window.innerHeight - context.height;
        }
        return { x: x, y: y };
    };
    return DialogFoundation;
}());
export { DialogFoundation };
